<template>
  <div class="flex flex-col">
    <image-preview
      v-if="ticketImageToPreview"
      :imageUrl="ticketImageToPreview"
      @close="ticketImageToPreview = null"
    />

    <h2>Inventario</h2>

    <div v-if="hasPermission('edit_inventories')">
      <button
        v-if="!showForm"
        class="secondary-button block mt-2"
        @click.prevent="showForm = true"
      >
        + Agregar Inventario
      </button>

      <button v-else class="cancel-button mt-2" @click.prevent="closeForm()">
        Cancelar
      </button>
    </div>

    <form v-if="showForm" class="card mt-4" @submit.prevent="createInventory">
      <button class="secondary-button" @click.prevent="createItem()">
        + Agregar Insumo
      </button>
      <validation-errors
        :errors="errorFor(`diet_subcategories`)"
      ></validation-errors>

      <div
        v-for="(subcategory, i) in form.diet_subcategories"
        :key="i"
        class="card shadow mt-4"
      >
        <div class="flex flex-col w-full">
          <label for="category">Categoría</label>
          <custom-select
            id="category"
            v-model="form.diet_subcategories[i].category_id"
            :options="getCategoryOptions()"
            @change="form.diet_subcategories[i].id = null"
          />
        </div>

        <div class="flex flex-col w-full mt-4">
          <label for="subcategory">Subcategoría</label>
          <custom-select
            id="subcategory"
            v-model="form.diet_subcategories[i].id"
            :disabled="form.diet_subcategories[i].category_id === null"
            :options="
              getSubcategoryOptions(
                getSubCategories(form.diet_subcategories[i].category_id)
              )
            "
          />
          <validation-errors
            :errors="errorFor(`diet_subcategories.${i}.id`)"
          ></validation-errors>
        </div>

        <div class="flex flex-col w-full mt-4">
          <label for="quantity">Cantidad (gramos)</label>
          <input
            id="quantity"
            type="number"
            step="any"
            v-model="form.diet_subcategories[i].quantity"
          />
          <validation-errors
            :errors="errorFor(`diet_subcategories.${i}.quantity`)"
          ></validation-errors>
        </div>

        <div class="flex flex-col w-full mt-4">
          <label for="quantity">Precio</label>
          <input
            id="quantity"
            type="number"
            step="any"
            v-model="form.diet_subcategories[i].price"
          />
          <validation-errors
            :errors="errorFor(`diet_subcategories.${i}.price`)"
          ></validation-errors>
        </div>

        <button
          class="delete-button mt-2 block"
          @click.prevent="form.diet_subcategories.splice(i, 1)"
        >
          Eliminar
        </button>
      </div>

      <div v-if="!newImage" class="mt-6">
        <input
          class="hidden"
          type="file"
          name="image"
          id="ticket"
          ref="image"
          accept="image/*"
          @change="getImage"
        />

        <label for="ticket" class="secondary-button font-normal"
          >+ Agregar Imagen de Ticket</label
        >
      </div>

      <div v-else class="mt-6">
        <p class="text-sm">{{ imageName }}</p>
        <button
          class="delete-button"
          @click.prevent="
            newImage = null;
            imageName = null;
          "
        >
          Remover
        </button>
      </div>

      <validation-errors :errors="errorFor(`ticket_image`)"></validation-errors>

      <primary-button type="submit" class="mt-6 mx-0" :loading="loadingForm"
        >Agregar
      </primary-button>
    </form>

    <div v-if="!loading" class="flex flex-col">
      <div v-if="inventories" class="card mt-6">
        <p v-if="inventories.length === 0">No hay inventario</p>

        <div v-for="category in aggregatedData" :key="category.id" class="my-2">
          <p class="font-medium">{{ category.name }}</p>

          <table class="table-auto mt-2 bg-white">
            <tbody>
              <div
                v-for="subcategory in category.subcategories"
                :key="subcategory.id"
                class="mt-2"
              >
                <tr class="border">
                  <td class="p-2 border-r">
                    {{ subcategory.name }}
                  </td>
                  <td class="p-2">
                    {{ subcategory.totalQuantity.toLocaleString() }}g
                  </td>
                </tr>

                <inventory-form
                  :subcategory="subcategory"
                  @quantityUpdated="
                    loadInventories();
                    loadInventoryHistories();
                  "
                />
              </div>
            </tbody>
          </table>
        </div>
      </div>

      <h2 class="font-medium mt-6">Historial</h2>

      <div class="card mt-2">
        <table class="table-auto text-sm mt-2 bg-white">
          <thead class="text-left">
            <tr class="bg-gray-100">
              <th class="p-2 rounded-l-lg">Categoría</th>
              <th class="p-2">Subategoría</th>
              <th class="p-2">Cantidad</th>
              <th class="p-2">Fecha</th>
              <th class="p-2">Razón</th>
              <th class="p-2 rounded-r-lg">Precio</th>
              <th class="p-2 rounded-r-lg">Ticket</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="history in inventoryHistories" :key="history.id">
              <td class="p-2">
                {{ history.category_name }}
              </td>
              <td class="p-2">
                {{ history.subcategory_name }}
              </td>
              <td class="p-2">
                {{ history.quantity_change.toLocaleString() }}g
              </td>
              <td class="p-2">
                {{ formatDatetime(history.created_at) }}
              </td>
              <td class="p-2">{{ history.reason }}</td>
              <td class="p-2">
                {{ integerToCurrency(history.price) }}
              </td>

              <td class="p-2">
                <div class="flex">
                  <button
                    v-if="history.ticket_image_url"
                    class="secondary-button"
                    @click.prevent="ticketImageToPreview = history.ticket_image_url"
                  >
                    Ver imagen
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="onPageChange"
      />
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import InventoryForm from "@/components/models/InventoryForm";
import ImagePreview from "@/components/modals/ImagePreview";
import Pagination from "@/components/ui/Pagination.vue";
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import deleteMixin from "@/mixins/deleteMixin";
import { is422 } from "@/utils/response";
import { formatDate, formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Inventories",

  components: {
    InventoryForm,
    ImagePreview,
    Pagination,
  },

  data() {
    return {
      inventories: null,
      inventoryHistories: null,
      loading: false,
      loadingForm: false,
      showForm: false,
      dietCategories: null,
      form: {
        branch_id: null,
        diet_subcategories: [],
      },
      currentPage: 1,
      totalPages: 0,
      subcategoryToUpdate: null,
      newImage: null,
      imageName: null,
      ticketImageToPreview: null
    };
  },

  computed: {
    aggregatedData() {
      let categories = {};

      this.inventories.forEach((item) => {
        const cat = item.diet_subcategory.diet_category;
        if (!categories[cat.id]) {
          categories[cat.id] = {
            id: cat.id,
            name: cat.name,
            subcategories: {},
          };
        }
        const subcat = item.diet_subcategory;
        if (!categories[cat.id].subcategories[subcat.id]) {
          categories[cat.id].subcategories[subcat.id] = {
            id: subcat.id,
            name: subcat.name,
            totalQuantity: 0,
            inventoryId: item.id,
          };
        }
        categories[cat.id].subcategories[subcat.id].totalQuantity +=
          item.quantity;
      });

      return Object.values(categories).map((category) => ({
        ...category,
        subcategories: Object.values(category.subcategories),
      }));
    },
  },

  mixins: [validationErrors, deleteMixin],

  created() {
    this.$store.commit("setSelectedTab", "inventories");

    this.loadDietCategories();
    this.loadInventories();
    this.loadInventoryHistories();
  },

  methods: {
    formatDate,

    formatDatetime,

    integerToCurrency,

    async loadDietCategories() {
      this.loading = true;

      try {
        this.dietCategories = await api.resources.getDietCategories();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: this.previousRoute });
      }

      this.loading = false;
    },

    async loadInventories() {
      this.loading = true;

      try {
        this.inventories = await api.inventories.getByBranch(
          this.$route.params.branchId
        );
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadInventoryHistories() {
      this.loading = true;

      try {
        const response = await api.inventoryHistories.getByBranch(
          this.$route.params.branchId,
          this.currentPage
        );
        this.inventoryHistories = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    getCategoryOptions() {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.dietCategories.forEach((category) => {
        options.push({
          value: category.id,
          label: category.name,
        });
      });

      return options;
    },

    getSubcategoryOptions(subcategories) {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      subcategories.forEach((category) => {
        options.push({
          value: category.id,
          label: category.name,
        });
      });

      return options;
    },

    closeForm() {
      this.showForm = false;
      this.form.diet_subcategories = [];
    },

    createItem() {
      this.form.diet_subcategories.push({
        id: null,
        category_id: null,
        quantity: "",
        price: "",
      });
    },

    getSubCategories(categoryId) {
      if (categoryId === null) {
        return [];
      }

      var subcategories = [];

      this.dietCategories.forEach((category) => {
        if (category.id === categoryId) {
          subcategories = category.diet_subcategories;
        }
      });

      return subcategories;
    },

    getImage(e) {
      let image = e.target.files[0];
      if (image) {
        this.imageName = image.name;

        let url = URL.createObjectURL(image);
        let img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(url);
          let reader = new FileReader();
          reader.onload = (e) => {
            this.newImage = image;
          };
          reader.readAsDataURL(image);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          alert("Unable to load image");
        };

        img.src = url;
      }
    },

    validateForm() {
      this.errors = {};

      if (this.form.diet_subcategories.length === 0) {
        this.errors["diet_subcategories"] = [
          "Debes agregar al menos un insumo",
        ];
      }

      this.form.diet_subcategories.forEach((sc, i) => {
        if (!sc.id) {
          this.errors[`diet_subcategories.${i}.id`] = ["Campo requerido"];
        }

        if (!sc.quantity) {
          this.errors[`diet_subcategories.${i}.quantity`] = ["Campo requerido"];
        }

        if (!sc.price) {
          this.errors[`diet_subcategories.${i}.price`] = ["Campo requerido"];
        }
      });

      if (!this.newImage) {
        this.errors["ticket_image"] = ["Debes agregar una imagen del ticket"];
      }

      return Object.keys(this.errors).length === 0;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadInventoryHistories();
    },

    async createInventory() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      this.loadingForm = true;

      this.form.branch_id = this.$route.params.branchId;

      const formData = new FormData();

      formData.append("branch_id", this.form.branch_id);
      formData.append("image", this.newImage);

      this.form.diet_subcategories.forEach((subcategory, index) => {
        formData.append(`diet_subcategories[${index}][id]`, subcategory.id);
        formData.append(
          `diet_subcategories[${index}][quantity]`,
          subcategory.quantity
        );
        formData.append(
          `diet_subcategories[${index}][price]`,
          subcategory.price
        );
      });

      try {
        const response = await api.inventories.createWithImage(formData);
        this.$store.dispatch("successSnack", response.message);
        this.closeForm();
        this.loadInventories();
        this.loadInventoryHistories();
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.newImage = null;
      this.imageName = null;
      this.loadingForm = false;
    },
  },
};
</script>